<section class="min-h-screen bg-base-200 px-4 py-6 sm:px-6">
  <div
    class="mx-auto flex min-h-[calc(100vh-3rem)] max-w-5xl items-center justify-center"
  >
    <div
      class="grid w-full max-w-4xl overflow-hidden rounded-[calc(var(--radius-box)*2)] border border-base-300 bg-base-100 shadow-xl lg:grid-cols-[1.05fr_0.95fr]"
    >
      <div
        class="hidden border-r border-base-300 bg-gradient-to-br from-base-100 to-base-200/70 p-10 lg:flex lg:flex-col lg:justify-between"
      >
        <div class="space-y-8">
          <div class="flex items-center gap-4">
            <div
              class="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-content"
            >
              <span class="brand-logo-text text-lg font-bold">
                {{ appConfig.shortName }}
              </span>
            </div>
            <div>
              <p class="text-xs uppercase tracking-[0.24em] text-base-content/45">
                Admin Workspace
              </p>
              <h1 class="brand-logo-text text-xl font-bold">{{ appConfig.name }}</h1>
            </div>
          </div>

          <div class="space-y-4">
            <div class="badge badge-outline badge-primary px-4 py-3">
              Assigned users only
            </div>
            <h2 class="max-w-md text-4xl font-black leading-tight text-base-content">
              Clean access for store and operations control.
            </h2>
            <p class="max-w-md text-sm leading-7 text-base-content/60">
              Sign in to manage inventory, catalog, stores, and operational
              workflows from one secure admin panel.
            </p>
          </div>
        </div>

        <div class="rounded-2xl border border-base-300 bg-base-100/80 p-4">
          <p class="text-xs uppercase tracking-[0.2em] text-base-content/45">
            Access policy
          </p>
          <p class="mt-2 text-sm leading-6 text-base-content/65">
            Accounts are provisioned by a superadmin. Self-signup is disabled.
          </p>
        </div>
      </div>

      <div class="flex items-center justify-center p-5 sm:p-8 lg:p-10">
        <div class="w-full max-w-md space-y-6">
          <div class="flex items-center gap-4 lg:hidden">
            <div
              class="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-content"
            >
              <span class="brand-logo-text text-lg font-bold">
                {{ appConfig.shortName }}
              </span>
            </div>
            <div>
              <p class="text-xs uppercase tracking-[0.24em] text-base-content/45">
                Secure Sign In
              </p>
              <h2 class="brand-logo-text text-xl font-bold">{{ appConfig.name }}</h2>
            </div>
          </div>

          <div class="space-y-2">
            <h3 class="text-3xl font-black text-base-content">
              {{ recoveryMode ? 'Reset your password' : 'Welcome back' }}
            </h3>
            <p class="text-sm leading-6 text-base-content/60">
              @if (recoveryMode) {
                Enter your assigned admin email and we'll send you a secure
                recovery link.
              } @else {
                Use your assigned admin account to continue.
              }
            </p>
          </div>

          <div class="rounded-2xl border border-base-300 bg-base-200/50 p-4">
            <div class="flex items-start gap-3">
              <i class="fa-solid fa-shield-halved mt-1 text-primary"></i>
              <p class="text-sm leading-6 text-base-content/65">
                @if (recoveryMode) {
                  For your security, we won't confirm whether an email is
                  registered.
                } @else {
                  Restricted admin access. Contact a superadmin if your account
                  has not been assigned yet.
                }
              </p>
            </div>
          </div>

          <div class="grid gap-5">
            <label class="form-control w-full">
              <div class="label pb-2">
                <span class="label-text font-medium">Email</span>
              </div>
              <label class="input input-bordered flex items-center gap-3">
                <i class="fa-regular fa-envelope text-base-content/40"></i>
                <input
                  type="email"
                  placeholder="admin@company.com"
                  class="grow"
                  [(ngModel)]="email"
                  autocomplete="username"
                />
              </label>
            </label>

            @if (recoveryMode) {
              <button
                type="button"
                class="btn btn-primary btn-lg"
                (click)="requestPasswordReset()"
                [disabled]="recoverySubmitting"
              >
                @if (recoverySubmitting) {
                  <span class="loading loading-spinner loading-sm"></span>
                } @else {
                  <i class="fa-regular fa-paper-plane"></i>
                }
                Send reset link
              </button>

              <button
                type="button"
                class="btn btn-ghost"
                (click)="showLogin()"
                [disabled]="recoverySubmitting"
              >
                <i class="fa-solid fa-arrow-left"></i>
                Back to login
              </button>
            } @else {
              <label class="form-control w-full">
                <div class="label pb-2">
                  <span class="label-text font-medium">Password</span>
                  <button
                    type="button"
                    class="btn btn-link btn-sm h-auto min-h-0 px-0 text-primary no-underline"
                    (click)="showPasswordRecovery()"
                  >
                    Forgot password?
                  </button>
                </div>
                <label class="input input-bordered flex items-center gap-3 pr-2">
                  <i class="fa-solid fa-lock text-base-content/40"></i>
                  <input
                    [type]="showPassword ? 'text' : 'password'"
                    placeholder="Enter your password"
                    class="grow"
                    [(ngModel)]="password"
                    autocomplete="current-password"
                  />
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm btn-square text-base-content/40 hover:text-base-content"
                    (click)="showPassword = !showPassword"
                    aria-label="Toggle password visibility"
                  >
                    <i class="fa-regular" [ngClass]="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                  </button>
                </label>
              </label>

              <p class="text-xs leading-5 text-base-content/55">
                Super Admin and Admin accounts only. Riders and customers must
                use their dedicated apps.
              </p>

              <button
                type="button"
                class="btn btn-primary btn-lg"
                (click)="login()"
                [disabled]="submitting"
              >
                @if (submitting) {
                  <span class="loading loading-spinner loading-sm"></span>
                } @else {
                  <i class="fa-solid fa-right-to-bracket"></i>
                }
                Login
              </button>
            }
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
